import { useNavigate } from "react-router-dom";
import { IoIosArrowBack } from "react-icons/io";
import {
    IoLocationOutline,
    IoCallOutline,
    IoCopyOutline,
    IoMailOutline,
    IoSearchOutline,
    IoChevronForwardOutline,
    IoPersonOutline,
} from "react-icons/io5";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getSessionLocation } from "../../redux/ApiSlice/LocationSlice";
import {
    searchInviteNeighbors,
    resetInviteSearch,
} from "../../redux/ApiSlice/Neighborhood/NeighborInviteSearchSlice";

const INVITE_OPTIONS = [
    // { id: "share", icon: IoCallOutline, label: "Share your neighborhood" },
    { id: "copy", icon: IoCopyOutline, label: "Copy invite link" },
    { id: "email", icon: IoMailOutline, label: "Invite by Email" },
];

const NeighborInvite = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [searchTerm, setSearchTerm] = useState("");
    const [copied, setCopied] = useState(false);
    const debounceRef = useRef(null);

    const { User_profile } = useSelector((state) => state.AccountProfile);
    const { sessionLocation } = useSelector((state) => state.LocationAddress);
    const { results, count, next, loading, loadingMore, error, message } =
        useSelector((state) => state.NeighborInviteSearch);

    useEffect(() => {
        if (!sessionLocation) {
            dispatch(getSessionLocation());
        }
    }, [dispatch, sessionLocation]);

    // Initial load — fetch all neighbors in area with no query
    useEffect(() => {
        dispatch(searchInviteNeighbors({ q: "", page: 1 }));
        return () => {
            dispatch(resetInviteSearch());
        };
    }, [dispatch]);

    // Debounced search on query change
    useEffect(() => {
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            dispatch(searchInviteNeighbors({ q: searchTerm, page: 1 }));
        }, 400);
        return () => clearTimeout(debounceRef.current);
    }, [searchTerm, dispatch]);

    const handleLoadMore = () => {
        if (!next || loadingMore) return;
        const url = new URL(next);
        const page = Number(url.searchParams.get("page")) || 2;
        dispatch(searchInviteNeighbors({ q: searchTerm, page }));
    };

    const locationText = (() => {
        if (sessionLocation?.city || sessionLocation?.country) {
            return `${sessionLocation?.city ?? ""}, ${sessionLocation?.country ?? ""}`.replace(/^,\s*|,\s*$/g, "").trim();
        }
        if (User_profile?.session_location?.city || User_profile?.session_location?.country) {
            return `${User_profile.session_location.city ?? ""}, ${User_profile.session_location.country ?? ""}`.replace(/^,\s*|,\s*$/g, "").trim();
        }
        if (User_profile?.city || User_profile?.country) {
            return `${User_profile?.city ?? ""}, ${User_profile?.country ?? ""}`.replace(/^,\s*|,\s*$/g, "").trim();
        }
        return "No Location";
    })();

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(window.location.origin + "/neighborhood");
            setCopied(true);
            setTimeout(() => setCopied(false), 3000);
        } catch {
            // fallback silently
        }
    };

    const handleEmail = () => {
        const link = window.location.origin + "/neighborhood";
        const subject = encodeURIComponent("Join me on Chaa Chingg!");
        const body = encodeURIComponent(`Hey! I'd like to invite you to join my neighborhood on Chaa Chingg.\n\nClick the link below to get started:\n${link}`);
        window.location.href = `mailto:?subject=${subject}&body=${body}`;
    };

    const handleOption = (id) => {
        if (id === "copy") handleCopy();
        if (id === "email") handleEmail();
    };

    return (
        <div className="relative h-full flex flex-col">
            {/* Header */}
            <div className="sticky top-0 bg-white dark:bg-[#1E1D21] pb-[2px] z-10">
                <div className="bg-[#BDE6FF] shadow-md rounded-b-xl px-2 py-1">
                    <IoIosArrowBack
                        onClick={() => navigate("/neighborhood")}
                        className="absolute left-2 top-1/2 -translate-y-1/2 text-[24px] cursor-pointer text-black"
                    />
                    <div className="flex flex-col items-center justify-center">
                        <p className="font-bold text-[18px] text-black">
                            Invite Neighbors
                        </p>
                        <div className="flex items-center text-[10px] text-black">
                            <IoLocationOutline className="text-[14px] ml-[-3px]" />
                            <span className="ml-1 text-[10px] line-clamp-1">
                                {locationText}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="flex-1 overflow-y-auto">
                {/* Search */}
                <div className="px-4 mt-4 border-b border-gray-300 dark:border-gray-800/50 pb-4">
                    <p className="text-[13px] font-semibold text-gray-500 mb-2">Search neighbors</p>
                    <div className="relative">
                        <input
                            type="text"
                            placeholder="Name or username"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-4 pr-10 py-2 text-[14px] dark:bg-[#333333] dark:text-white border border-gray-300 dark:border-gray-800/50 rounded-full focus:outline-none focus:ring-1 ring-blue-300"
                        />
                        <IoSearchOutline className="absolute right-3 top-1/2 -translate-y-1/2 text-[18px] text-gray-400" />
                    </div>
                </div>

                {/* Invite options */}
                <div className="px-4 mt-2 pb-2">
                    <p className="text-[14px] font-semibold text-black dark:text-white mb-3">
                        Invite neighbors to join Chaa Chingg
                    </p>
                    <div className="flex flex-col gap-3">
                        {INVITE_OPTIONS.map(({ id, icon: Icon, label }) => (
                            <button
                                key={id}
                                onClick={() => handleOption(id)}
                                className="flex items-center justify-between bg-[#BDE6FF] rounded-2xl px-4 py-3"
                            >
                                <div className="flex items-center gap-3">
                                    <Icon className="text-[20px] text-black" />
                                    <span className="text-[13px] font-medium text-black">
                                        {label}
                                    </span>
                                </div>
                                <IoChevronForwardOutline className="text-[15px] text-black" />
                            </button>
                        ))}
                    </div>
                </div>
                {/* Neighbor search results */}
                <div className="px-4 mt-2">
                    {loading ? (
                        <div className="flex flex-col gap-3">
                            {[...Array(4)].map((_, i) => (
                                <div key={i} className="flex items-center gap-3 animate-pulse">
                                    <div className="w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-700 shrink-0" />
                                    <div className="flex-1">
                                        <div className="h-3 w-32 bg-gray-200 dark:bg-gray-700 rounded mb-1" />
                                        <div className="h-2 w-20 bg-gray-200 dark:bg-gray-700 rounded" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : error ? (
                        <p className="text-[13px] text-red-500 text-center py-4">
                            {typeof error === "string" ? error : error?.message ?? "Something went wrong."}
                        </p>
                    ) : message ? (
                        <p className="text-[13px] text-gray-400 dark:text-gray-500 text-center py-4">{message}</p>
                    ) : results.length > 0 ? (
                        <>
                            <p className="text-[12px] text-gray-400 dark:text-gray-500 mb-2">
                                {count} neighbor{count !== 1 ? "s" : ""} found
                            </p>
                            <div className="flex flex-col gap-3 mb-4">
                                {results.map((user) => (
                                    <div
                                        key={user.id}
                                        className="flex items-center justify-between gap-3"
                                    >
                                        <div className="flex items-center gap-3 " onClick={() => navigate(`/account/${user.id}`)}>
                                            {user.avatar ? (
                                                <img
                                                    src={user.avatar}
                                                    alt={user.full_name}
                                                    className="w-10 h-10 cursor-pointer rounded-full object-cover shrink-0"
                                                />
                                            ) : (
                                                <div className="w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center shrink-0">
                                                    <IoPersonOutline className="text-[18px] text-gray-400" />
                                                </div>
                                            )}
                                            <div>
                                                <p className="text-[13px] cursor-pointer font-semibold text-black dark:text-white line-clamp-1">
                                                    {user.full_name || `${user.first_name} ${user.last_name}`.trim()}
                                                </p>
                                                {/* <p className="text-[11px] text-gray-400 dark:text-gray-500 line-clamp-1">
                                                    @{user.first_name}
                                                </p> */}
                                            </div>
                                        </div>
                                        {/* <button className="shrink-0 text-[12px] font-semibold text-blue-500 border border-blue-400 rounded-full px-3 py-1">
                                            Invite
                                        </button> */}
                                    </div>
                                ))}
                            </div>
                            {next && (
                                <button
                                    onClick={handleLoadMore}
                                    disabled={loadingMore}
                                    className="w-full text-[13px] text-blue-500 font-medium py-2 text-center"
                                >
                                    {loadingMore ? "Loading..." : "Load more"}
                                </button>
                            )}
                        </>
                    ) : null}
                </div>


            </div>

            {/* Copied toast */}
            {copied && (
                <div className="absolute flex items-center justify-center bg-black/50 h-full w-full left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 z-50">
                    <div className="bg-white rounded-2xl px-6 py-2 shadow-md whitespace-nowrap">
                        <p className="text-[14px] font-bold text-black">Invite link copied!</p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default NeighborInvite;
